import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FileText, Plus, RefreshCw, Send, Car, User, AlertCircle, Loader2, Clock, CheckCircle,
} from 'lucide-react';
import {
    fetchOpenQuotes, fetchAwaitingQuotes, sendQuoteForApproval, clearQuoteError,
} from '../store/slices/quoteSlice';
import { OPEN_QUOTES_PAGE_SIZE } from '../services/quoteApi';
import PagedSearchControls from './PagedSearchControls';
import LaneLoading from './LaneLoading';
import useSearchBox from '../hooks/useSearchBox';
import { filterByNameOrRego } from '../utils/localSearch';
import {
    fetchJob, closeViewModal, openViewModal,
    addWorkItem, updateWorkItem, removeWorkItem, addJobNote,
    fetchWorkCategories, fetchCategoryTypes,
} from '../store/slices/jobSlice';
import Layout from './Layout';
import JobDetailModal from './JobDetailModal';
import { useOpenInvoice } from '../hooks/useRecordNavigation';
import QuoteFormModal from './QuoteFormModal';
import HistoricalQuotes from './quotes/HistoricalQuotes';
import { formatAud } from '../utils/money';

/**
 * The quotes board — priced work waiting on a customer's answer.
 *
 * Two lanes, and the split is not this page's opinion: the server serves them from two
 * endpoints — "not sent" is `/quotes/open`, "waiting on the customer" is
 * `/quotes/awaiting-customer` — on the same `customerApprovalPending` flag that draws
 * the waiting-on-customer badge on the jobs board. Deciding it here from a status
 * string would be a second, weaker copy of a rule the front end does not own.
 *
 * 🔑 The two lanes are shaped differently on purpose. "Not sent" is where a migration
 * lands thousands of quotes at once, so it comes a page of 1,000 at a time with a
 * search box that filters THAT PAGE locally by name or rego — nothing about the search
 * goes to the server. "Waiting on the customer" is the chase list: unfiltered and
 * unpaged, because a chase list with a page 2 is one somebody has stopped reading.
 *
 * A quote leaves this board the moment the customer answers — approved, partially
 * approved or declined alike — because the server converts it to a CREATED job and it
 * stops matching the quotes query. That is why there is no third lane here: a declined
 * quote is a conversation the workshop needs to have, and hiding it on a board nobody
 * watches is how it gets forgotten.
 */

const LANES = [
    {
        id: 'created',
        title: 'Created',
        hint: 'Not sent yet — add priced work items, then send',
        accent: 'border-gray-300 bg-gray-50',
        badge: 'bg-gray-200 text-gray-700',
    },
    {
        id: 'sent',
        title: 'Sent for approval',
        hint: 'Waiting on the customer',
        accent: 'border-violet-300 bg-violet-50/60',
        badge: 'bg-violet-200 text-violet-800',
    },
];

/**
 * The not-sent lane's search, over the loaded page, in the browser.
 *
 * Customer name or rego, case-insensitive substring. This is the FAST half: it runs on
 * the keystroke over the twenty cards already here. The settled term goes to the
 * server as well — see useSearchBox — because the quote being looked for is usually on
 * one of the other pages.
 */
export function filterQuotesLocally(quotes, search) {
    // The same rule every paged job list uses — one copy, in utils/localSearch.
    return filterByNameOrRego(quotes, search);
}

const QuoteCard = ({ quote, onOpen, onSend, sending }) => {
    // A quote with no priced work cannot be sent — the server refuses it, and it is
    // kinder to say so on the card than to let staff press a button that fails.
    const hasWorkItems = (quote.workItemCount ?? 0) > 0;
    // 🔑 Approval IS an email: without one the server refuses, and pressing Send was the
    // only way to find out.
    //
    // 🔴 But ABSENT is not EMPTY. customerEmail was added to JobResponse after this
    // screen shipped, so a browser running against a server that has not been restarted
    // gets no such field — and reading undefined as "no email" put a red warning on
    // quotes that HAD one and had already been sent. Only claim it is missing when the
    // server actually said so; otherwise stay quiet and let the send surface the
    // server's own sentence.
    const emailKnown = Object.prototype.hasOwnProperty.call(quote, 'customerEmail');
    const missingEmail = emailKnown && !quote.customerEmail;
    const isSent = !!quote.customerApprovalPending;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onOpen(quote)}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(quote); }}
            className="h-full flex flex-col text-left bg-white rounded-xl border border-gray-200 p-3 hover:border-violet-300 hover:shadow-sm transition-all cursor-pointer"
        >
            {/* Nothing here truncates. A tile is 230px wide and a customer name or a
                vehicle can easily be longer than that — clipping either one hides the
                two facts the card exists to show, and "Honda Civic (I1A9..." tells a
                service advisor nothing. The text wraps instead and the tile grows; the
                action is pinned to the bottom so a row of tiles still lines up.

                The job number goes on its own line rather than beside the name: side by
                side, a long name squeezes it to nothing. */}
            <div className="flex items-start gap-1.5">
                <User size={13} className="text-gray-400 shrink-0 mt-0.5" />
                <p className="font-semibold text-gray-900 text-sm break-words min-w-0">
                    {quote.customerName}
                </p>
            </div>
            <div className="flex items-start gap-1.5 mt-1">
                <Car size={12} className="text-gray-400 shrink-0 mt-0.5" />
                {/* vehicleDescription is Vehicle.getDisplayName(), which ALREADY ends in
                    the rego — "Honda Civic (I1A9BQ)". Appending quote.vehicleRego printed
                    it twice and was what pushed the line off the edge. */}
                <p className="text-xs text-gray-500 break-words min-w-0">
                    {quote.vehicleDescription}
                </p>
            </div>
            <p className="text-[11px] font-mono text-gray-400 mt-1.5 break-all">
                {quote.jobNumber}
            </p>

            <div className="mt-2.5 flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500">
                    {hasWorkItems
                        ? `${quote.workItemCount} item${quote.workItemCount === 1 ? '' : 's'}`
                        : 'No work items yet'}
                </span>
                <span className="text-sm font-semibold text-gray-900">
                    {formatAud(quote.totalEstimatedCost)}
                </span>
            </div>

            {/* The action sits in its own mt-auto wrapper rather than carrying the
                margin itself: putting padding on the button would fight its own py-1.5
                and push the label off centre. This way every tile in a row ends at the
                same place however far the customer name wraps. */}
            <div className="mt-auto pt-2.5">
                {isSent ? (
                    <div className="flex items-center gap-1.5 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1.5">
                        <Clock size={12} className="shrink-0" /> Waiting on the customer
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onSend(quote); }}
                        disabled={!hasWorkItems || missingEmail || sending}
                        title={!hasWorkItems ? 'Add a priced work item first'
                            : missingEmail ? 'This customer has no email address — add one on the job card'
                            : 'Send every priced item to the customer'}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-semibold hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                        Send for approval
                    </button>
                )}
                {missingEmail && (
                    <p className="mt-1.5 text-[11px] text-amber-700">
                        No email for this customer — open the quote to add one.
                    </p>
                )}
            </div>
        </div>
    );
};

const QuotesPage = () => {
    const dispatch = useDispatch();
    // Following a job's invoice link, without a page reload.
    const openInvoice = useOpenInvoice();
    const { open, awaiting, error, operationLoading, operationError } = useSelector((s) => s.quotes);
    const loading = open.loading || awaiting.loading;
    const { selectedJob, showViewModal, operationLoading: jobOperationLoading,
        detailLoading: jobDetailLoading,
        workCategories, categoryTypes } = useSelector((s) => s.jobs);

    const [showForm, setShowForm] = useState(false);
    // 🔑 A VIEW, not a panel tucked under the lanes. A migration brings thousands of
    // imported quotes — 10,589 in the real one — and a collapsed link below two
    // full-height lanes is not somewhere anybody finds them.
    const [view, setView] = useState('active');
    const [sendingUuid, setSendingUuid] = useState(null);
    // 🔑 Sending EMAILS the customer. It is the one action on this board that leaves the
    // building, and it cannot be taken back — so it is confirmed, and the confirmation
    // names who is about to be written to.
    const [confirmSend, setConfirmSend] = useState(null);
    // The not-sent lane's page and its search. Two speeds: the text narrows the loaded
    // page here, the settled term asks the server — see useSearchBox.
    const [openPage, setOpenPage] = useState(0);
    const openBox = useSearchBox();

    // A new term starts again from page one: page four of "everything" is not page four
    // of "Smith", and asking for it would answer with an empty lane.
    useEffect(() => { setOpenPage(0); }, [openBox.q]);

    const load = useCallback(() => {
        dispatch(fetchOpenQuotes({ q: openBox.q ?? '', page: openPage, size: OPEN_QUOTES_PAGE_SIZE }));
        dispatch(fetchAwaitingQuotes());
    }, [dispatch, openPage, openBox.q]);

    useEffect(() => {
        load();
        // The work-item editor inside the job detail modal needs these; they are org
        // config and are fetched once per mount, exactly as the jobs board does it.
        dispatch(fetchWorkCategories());
        dispatch(fetchCategoryTypes());
    }, [dispatch, load]);

    const openQuote = (quote) => {
        // openViewModal sets selectedJob as well as the flag; fetchJob then replaces
        // the list summary with the full detail (work items, notes, approvals).
        dispatch(openViewModal(quote));
        dispatch(fetchJob(quote.uuid));
    };

    const handleSend = async (quote) => {
        setConfirmSend(null);
        setSendingUuid(quote.uuid);
        dispatch(clearQuoteError());
        try {
            await dispatch(sendQuoteForApproval(quote.uuid)).unwrap();
            load();
            // Sent from inside the open quote: refresh it so the work items show
            // AWAITING CONFIRMATION without the modal being closed and reopened.
            if (showViewModal && selectedJob?.uuid === quote.uuid) {
                dispatch(fetchJob(quote.uuid));
            }
        } catch {
            // operationError renders the server's own sentence below.
        } finally {
            setSendingUuid(null);
        }
    };

    const handleCreated = (createdQuote) => {
        setShowForm(false);
        load();
        // Straight into the detail view: a quote is its priced work, and that is where
        // work items are added.
        openQuote(createdQuote);
    };

    // Refresh on close: adding work items or sending from inside the modal changes
    // which lane the card belongs in, and the board would otherwise show the old one.
    const handleCloseDetail = () => {
        dispatch(closeViewModal());
        load();
    };

    return (
        // Every desktop page wraps ITSELF in Layout — the routes in App.jsx do not do
        // it for them. Without this the board renders with no sidebar at all, which is
        // a dead end: there is no way back to Jobs or Bookings.
        <Layout>
            <div className="h-full flex flex-col bg-gray-50">
                <div className="px-4 py-3 border-b bg-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
                            <FileText size={18} className="text-violet-600" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900">Quotes</h1>
                            <p className="text-xs text-gray-500">
                                {open.total} not sent · {awaiting.quotes.length} awaiting a customer decision
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center bg-gray-100 rounded-xl p-0.5 mr-1">
                            {[['active', 'Active'], ['imported', 'Imported']].map(([key, label]) => (
                                <button key={key} type="button" onClick={() => setView(key)}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                            view === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={load} disabled={loading}
                                title="Refresh"
                                className="h-9 w-9 flex items-center justify-center bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 disabled:opacity-50">
                            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
                        </button>
                        <button type="button" onClick={() => setShowForm(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-semibold hover:bg-violet-700">
                            <Plus size={16} /> New Quote
                        </button>
                    </div>
                </div>

                {(error || operationError) && (
                    <div className="mx-4 mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                        <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                        <p className="text-sm text-red-700">{error || operationError}</p>
                    </div>
                )}

                {view === 'imported' ? <HistoricalQuotes /> : (

                <div className="flex-1 overflow-auto p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                        {LANES.map((lane) => {
                            const isOpenLane = lane.id === 'created';
                            // 🔑 The pill on the not-sent lane is the server's total of ALL
                            // not-sent quotes — not the page, and not what the local search
                            // has narrowed it to. That number is what a workshop
                            // mid-migration is asking.
                            const laneQuotes = isOpenLane
                                ? filterQuotesLocally(open.quotes, openBox.text)
                                : awaiting.quotes;
                            const pill = isOpenLane ? open.total : awaiting.quotes.length;
                            const laneLoading = isOpenLane ? open.loading : awaiting.loading;
                            return (
                                <div key={lane.id}
                                     data-testid={`lane-${lane.id}`}
                                     className={`rounded-2xl border-2 ${lane.accent} flex flex-col min-h-[300px]`}>
                                    <div className="px-4 py-3 flex items-center justify-between">
                                        <div>
                                            <h2 className="font-semibold text-gray-900 text-sm">{lane.title}</h2>
                                            <p className="text-[11px] text-gray-500 mt-0.5">{lane.hint}</p>
                                        </div>
                                        <span data-testid={`lane-count-${lane.id}`}
                                              className={`text-xs font-bold px-2 py-0.5 rounded-full ${lane.badge}`}>
                                            {pill}
                                        </span>
                                    </div>
                                    {/* The text narrows the twenty cards already here; the
                                        settled term asks the server, which holds the rest. */}
                                    {isOpenLane && (
                                        <PagedSearchControls
                                            className="px-4 pb-2"
                                            search={openBox.text}
                                            onSearch={openBox.setText}
                                            error={openBox.error}
                                            page={open.page}
                                            totalPages={open.totalPages}
                                            total={open.total}
                                            size={OPEN_QUOTES_PAGE_SIZE}
                                            onPage={setOpenPage}
                                            placeholder="Name or rego"
                                            searchLabel="Search unsent quotes"
                                            pagerLabel="unsent quotes"
                                            alwaysShowPager
                                        />
                                    )}
                                    {/* Tiles, not rows. A lane holds as many quotes as fit
                                        across its width before wrapping, so a workshop with a
                                        dozen outstanding quotes reads them at a glance instead
                                        of scrolling a single column. auto-fill (not auto-fit)
                                        keeps a lone tile at its natural width rather than
                                        stretching it across the whole lane, and content-start
                                        keeps rows packed to the top. */}
                                    <div className="flex-1 px-3 pb-3 overflow-y-auto grid gap-3 content-start
                                                    [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]">
                                        {/* The same card-shaped wait the job lanes use, so a
                                            lane that is loading never reads as a lane that is
                                            empty — including while a search is in flight. */}
                                        {laneLoading && (
                                            <LaneLoading className="col-span-full"
                                                         label={`Loading ${lane.title.toLowerCase()}`} />
                                        )}
                                        {!laneLoading && laneQuotes.length === 0 && (
                                            <p className="col-span-full text-center text-sm text-gray-400 py-8">
                                                {!isOpenLane ? 'Nothing awaiting a customer'
                                                    : openBox.text.trim() ? 'Nothing here matches'
                                                        : 'No quotes yet'}
                                            </p>
                                        )}
                                        {!laneLoading && laneQuotes.map((q) => (
                                            <QuoteCard key={q.uuid} quote={q} onOpen={openQuote}
                                                       onSend={setConfirmSend}
                                                       sending={sendingUuid === q.uuid && operationLoading} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-4 text-xs text-gray-500 flex items-center gap-1.5">
                        <CheckCircle size={13} className="text-emerald-500" />
                        Once the customer responds — approved, partly approved or declined — the
                        quote moves to the Jobs board, in the Queued lane.
                    </p>

                </div>

                )}

                {confirmSend && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                         role="dialog" aria-modal="true" aria-label="Send quote for approval">
                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
                            <h3 className="text-base font-bold text-gray-900">Send this quote?</h3>
                            <p className="text-sm text-gray-600 mt-2">
                                An email goes to{' '}
                                <strong className="font-semibold">{confirmSend.customerName}</strong>
                                {confirmSend.customerEmail && (
                                    <> at <span className="font-mono text-xs">{confirmSend.customerEmail}</span></>
                                )} asking them to approve every priced item on it.
                            </p>
                            {/* Said because it is the part that cannot be undone. */}
                            <p className="text-xs text-gray-500 mt-2">
                                It cannot be unsent. The quote moves to “Sent for approval”
                                and waits on their answer.
                            </p>
                            <div className="flex items-center justify-end gap-2 mt-4">
                                <button type="button" onClick={() => setConfirmSend(null)}
                                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900">
                                    Cancel
                                </button>
                                <button type="button" onClick={() => handleSend(confirmSend)}
                                        className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700">
                                    <Send size={14} /> Send for approval
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showForm && (
                    <QuoteFormModal onClose={() => setShowForm(false)} onCreated={handleCreated} />
                )}

                {showViewModal && selectedJob && (
                    <JobDetailModal
                        onOpenInvoice={openInvoice}
                        job={selectedJob}
                        operationLoading={jobOperationLoading}
                        detailLoading={jobDetailLoading}
                        workCategories={workCategories}
                        categoryTypes={categoryTypes}
                        jobResources={[]}
                        availableResources={[]}
                        resourcesLoading={false}
                        onClose={handleCloseDetail}
                        onAddWorkItem={(payload) =>
                            dispatch(addWorkItem({ jobUuid: selectedJob.uuid, data: payload }))}
                        onEditWorkItem={(payload, workItemUuid) => {
                            // ⚠️ (payload, uuid), not (uuid, payload) — the modal calls it
                            // that way round and swapping them silently edits nothing.
                            if (!workItemUuid) return;
                            dispatch(updateWorkItem({ jobUuid: selectedJob.uuid, workItemUuid, data: payload }));
                        }}
                        onRemoveWorkItem={(workItemUuid) =>
                            dispatch(removeWorkItem({ jobUuid: selectedJob.uuid, workItemUuid }))}
                        onAddNote={async ({ content, noteType, customerVisible, workItemUuid }) => {
                            const result = await dispatch(addJobNote({
                                jobUuid: selectedJob.uuid,
                                data: {
                                    content,
                                    noteType: noteType || 'PROGRESS_UPDATE',
                                    customerVisible: !!customerVisible,
                                    workItemUuid: workItemUuid || null,
                                },
                            }));
                            return addJobNote.fulfilled.match(result) ? result.payload : null;
                        }}
                        onRefreshJob={async () => {
                            // Must RETURN the refreshed job — the modal renders from what
                            // comes back, not from the store, after an inline edit.
                            const result = await dispatch(fetchJob(selectedJob.uuid));
                            return fetchJob.fulfilled.match(result) ? result.payload : null;
                        }}
                        // 🔑 Through the SAME confirmation as the board. Sending from
                        // inside the open quote is still an email leaving the building,
                        // and it was going straight out from here with nothing asked.
                        onSendQuote={setConfirmSend}
                    />
                )}
            </div>
        </Layout>
    );
};

export default QuotesPage;
